// Storage paths follow `<userId>/<fileId>.<ext>`. We reuse that UUID as the
// `file_metadata.id` so fileViewerUrl()'s CDN-URL parsing produces a `/v/{id}`
// that the proxy route can actually resolve. Two independent randomUUID()
// calls (one for the path, one for the row) previously diverged silently —
// the row would land in the DB, but the proxy URL the UI extracted from the
// CDN URL pointed at a UUID that didn't exist as a primary key. (See
// task 19251818… 2026-05-21 — orphan URL incident.)
function extractFileIdFromStoragePath(storagePath: string): string | null {
  const match = storagePath.match(
    /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\.[^/]+$/i,
  );
  return match ? match[1] : null;
}

/**
 * The file id carried by a storage URL we minted — the SAME convention
 * `confirmUpload` writes above (`<userId>/<fileId>.<ext>`), read back off the
 * URL's path. Null when the URL is unparseable or its object name predates the
 * convention (those rows have a randomUUID id the path never carried).
 *
 * Query and fragment are stripped first, because a SIGNED url ends `?token=…`
 * and the underlying matcher anchors on the extension. Lives HERE, in shared,
 * so the convention has exactly one home for every caller (the server's
 * upload door AND the composer door's owner check on a storage URL): a second copy
 * of this regex elsewhere is the orphan-URL incident waiting to happen again.
 *
 * A CANDIDATE, NOT AN AUTHORITY. The id it returns must still be looked up with
 * the owner's id before anything is read or cited — the URL comes off the wire.
 */
export function fileIdFromStorageUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const path = url.split('#')[0]!.split('?')[0]!;
  return extractFileIdFromStoragePath(path);
}

/** The door a PERSON opens a file or creation by: permanent, and governed by its sharing
 *  on every open (the web /v route asks the gate, then mints a 60 s pass). */
export function viewerPath(id: string): string {
  return `/v/${id}`;
}

/** The door the BOX reads a file or creation by: its HMAC token, the same gate, and a
 *  302 to a 5-minute pass (apps/server box-files-routes.handler.ts). */
export function boxFilePath(id: string): string {
  return `/api/box/files/${id}`;
}

export interface StorageObjectRef {
  bucket: 'uploads' | 'outputs';
  path: string;
}

/** Anchored: the whole PATHNAME is a storage object path, and the bucket is spelled literally. */
const STORAGE_OBJECT_PATH = /^\/storage\/v1\/(?:object|render\/image)\/(?:public|sign)\/(uploads|outputs)\/(.+)$/;
const RELATIVE_BASE = 'http://relative.invalid';

/**
 * The bucket and object path of a storage URL for one of the two user buckets:
 * public, signed, or a render transform. Null for anything else.
 *
 * It reads the URL's PATHNAME only (parsed, dot segments resolved), the object any
 * fetcher of this URL would request: a storage path inside the query or the fragment
 * names nothing, and a percent-encoded bucket name does not parse.
 *
 * A CANDIDATE, NOT AN AUTHORITY: this parses a URL, it does not pin the host and it
 * says nothing about who may read the object. Callers pin the host and ask the gate.
 */
export function storageObjectFromUrl(url: string | null | undefined): StorageObjectRef | null {
  if (!url) return null;
  let pathname: string;
  try {
    pathname = new URL(url, RELATIVE_BASE).pathname;
  } catch {
    return null;
  }
  const m = STORAGE_OBJECT_PATH.exec(pathname);
  if (!m) return null;
  try {
    return { bucket: m[1] as StorageObjectRef['bucket'], path: decodeURIComponent(m[2]!) };
  } catch {
    return null;
  }
}

/** How far a creation's lineage is followed: the same cap as the SQL gate's walk. */
export const MAX_LINEAGE_HOPS = 32;

/**
 * A creation's lineage HEAD (its latest version), the one walk every door takes:
 * follow `superseded_by` (internal id → internal id) at most MAX_LINEAGE_HOPS
 * times, stopping at the first row that names no successor or whose successor is
 * missing. The last row reached is the head. A cycle stops at the cap, never loops.
 *
 * It matches public.file_access's recursive walk (depth < 32, deepest row wins),
 * so the gate and the reader always agree on which row a public id means. The
 * caller supplies the row fetch (web client, server admin, a test fake).
 */
export async function resolveCreationHead<Row extends { superseded_by?: string | null }>(
  start: Row,
  fetchById: (id: string) => Promise<Row | null>,
): Promise<Row> {
  let head = start;
  for (let hops = 0; head.superseded_by && hops < MAX_LINEAGE_HOPS; hops += 1) {
    const next = await fetchById(head.superseded_by);
    if (!next) break;
    head = next;
  }
  return head;
}

/**
 * Where a creation's bytes live: its `storage_path`, else (a legacy Studio row
 * written before storage_path existed) the outputs object named in its stored
 * `url`. Creations live only in `outputs`, so a url naming another bucket is null.
 * The backfill fills storage_path before it rewrites that url.
 *
 * The url fallback is PINNED to the row owner's folder: its decoded path must sit
 * under `<user_id>/` with no `.` or `..` segment, so a legacy row's url can never
 * make a door serve another user's object. No owner, no fallback.
 */
export function creationStorageObject(
  row: { user_id: string | null | undefined; storage_path?: string | null; url?: string | null },
): { bucket: 'outputs'; path: string } | null {
  if (row.storage_path) return { bucket: 'outputs', path: row.storage_path };
  const fromUrl = storageObjectFromUrl(row.url);
  if (fromUrl?.bucket !== 'outputs' || !row.user_id) return null;
  const segments = fromUrl.path.split('/');
  const inOwnersFolder = segments[0] === row.user_id && segments.length > 1
    && segments.every((seg) => seg !== '.' && seg !== '..');
  return inOwnersFolder ? { bucket: 'outputs', path: fromUrl.path } : null;
}
